import React, { memo } from 'react';

import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import Skeleton from '@atlaskit/skeleton';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { TEMPLATE_TILE_WIDTH_IN_PX } from './modalWidth.constants.tsx';

const templatesLibraryTitle = defineMessage({
	id: 'component-view-template-library.templates-library.title',
	defaultMessage: 'Templates',
	description: 'Title for the template library modal',
});

const SKELETON_TILE_COUNT = 6;

const styles = cssMap({
	categoryButtonsWrapper: {
		marginBlockEnd: token('space.300'),
	},
	tilesGrid: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		gridTemplateColumns: `repeat(auto-fill, minmax(${TEMPLATE_TILE_WIDTH_IN_PX}px, 1fr))`,
		paddingBlockEnd: token('space.300'),
	},
	tileRoot: {
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		paddingTop: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		width: `${TEMPLATE_TILE_WIDTH_IN_PX}px`,
	},
	imageWrapper: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
		height: '146px',
		overflow: 'hidden',
	},
	textWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
	},
});

const CategoryBarSkeleton = () => (
	<Box
		xcss={styles.categoryButtonsWrapper}
		aria-hidden="true"
		testId="component-view-template-library.template-library-category-bar-skeleton"
	>
		<Skeleton height={32} width="560px" borderRadius={token('radius.small')} isShimmering />
	</Box>
);

const TemplateTileSkeleton = () => (
	<Box xcss={styles.tileRoot} aria-hidden="true" aria-busy="true">
		<Box xcss={styles.imageWrapper}>
			<Skeleton height={146} width="100%" borderRadius={0} isShimmering />
		</Box>
		<Box xcss={styles.textWrapper}>
			<Skeleton height={20} width="70%" borderRadius={token('radius.small')} isShimmering />
			<Skeleton height={16} width="90%" borderRadius={token('radius.small')} isShimmering />
		</Box>
	</Box>
);

export const TemplateLibrarySkeleton: React.MemoExoticComponent<() => React.JSX.Element> = memo(
	(): React.JSX.Element => {
		const { formatMessage } = useIntl();

		return (
			<>
				<ModalHeader hasCloseButton>
					<ModalTitle>{formatMessage(templatesLibraryTitle)}</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{fg('jpd_view_template_library_m2') ? <CategoryBarSkeleton /> : null}
					<Grid gap="space.200" xcss={styles.tilesGrid}>
						{Array.from({ length: SKELETON_TILE_COUNT }, (_, index) => (
							<TemplateTileSkeleton key={index} />
						))}
					</Grid>
				</ModalBody>
			</>
		);
	},
);
